import React, { useMemo, useRef, useState } from "react";
import { Alert, IconButton, Tooltip } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { exportTicketToPDF } from './ExportTicketPDF';
import { formatReportNumber } from './reportNumbers';

const parseUtcDate = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;

  const hasTimezoneSuffix = /[zZ]|[-+]\d{2}:\d{2}$/.test(s);
  const normalized = hasTimezoneSuffix ? s : `${s.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  return Number.isFinite(date.getTime()) ? date : null;
};

const formatDateTimeAtZone = (value, timeZone = "UTC") => {
  const date = parseUtcDate(value);
  if (!date) return "-";

  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(date);
  }
};

const parseJson = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(String(value));
  } catch {
    return null;
  }
};

const decodeHtml = (value) => {
  const s = String(value ?? "");
  if (!s) return "";

  if (typeof window !== "undefined" && window.document) {
    const txt = window.document.createElement("textarea");
    txt.innerHTML = s;
    return txt.value;
  }

  return s.replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)));
};

const parseTimeMs = (value) => {
  const date = parseUtcDate(value);
  if (!date) return null;
  const t = date.getTime();
  return Number.isFinite(t) ? t : null;
};

const formatDurationMinutes = (startIso, endIso) => {
  const startMs = parseTimeMs(startIso);
  const endMs = parseTimeMs(endIso);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return "-";
  return `${((endMs - startMs) / 60000).toFixed(2)} min`;
};

const getTimeToMake = (receivedAt, bumpedAt) => {
  return formatDurationMinutes(receivedAt, bumpedAt);
};

const toItemArray = (ticket) => {
  if (!ticket || !ticket.Item) return [];
  return Array.isArray(ticket.Item) ? ticket.Item : [ticket.Item];
};

const stationLabel = (station) => {
  if (!station) return "Unknown Station";
  if (station.station_name) return `${station.station_name} (ID: ${station.station_id})`;
  return `Station ${station.station_id}`;
};

const statusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "voided") return "bg-red-100 text-red-700 border-red-200";
  if (s === "bumped") return "bg-green-100 text-green-700 border-green-200";
  if (s === "parked") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-blue-100 text-blue-700 border-blue-200";
};

const courseCardBgClass = (course) => {
  return Number(course) % 2 === 0
    ? "bg-emerald-200 dark:bg-emerald-900/40"
    : "bg-slate-200 dark:bg-slate-800";
};

const isCourseSeparatorRow = (name, category, itemId, rawItem = null) => {
  const n = String(name || "").trim().toUpperCase();
  const c = String(category || "").trim().toUpperCase();
  const rawName = String(name || "").trim();
  const id = String(itemId || "").trim().toLowerCase();
  const rawFlag = !!rawItem?.isCourseSeparator;

  if (rawFlag) return true;
  if (id.startsWith("sep-auto-")) return true;
  if (c === "COURSE SEPARATOR") return true;
  if (n === "COURSE SEPARATOR" || n.includes("COURSE SEPARATOR")) return true;

  // Encore separator lines often come in as star-only names.
  if (/^\*+$/.test(rawName)) return true;

  // Defensive: separators often arrive as dash-only pseudo lines.
  const stripped = n.replace(/[-\s]/g, "");
  if (!stripped) return true;

  return false;
};

const isMessageRow = (name) => {
  const n = String(name || "").trim().toUpperCase();
  return n === "* MESSAGE *";
};

const isFirePseudoRow = (name) => {
  const n = String(name || "").trim().toUpperCase();
  return n === "------ FIRE ------";
};

export default function TicketViewer({ ticketDetail }) {
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState('');
  const exporting = useRef(false);
  const snapshots = useMemo(
    () => Array.isArray(ticketDetail?.ticket_snapshots) ? ticketDetail.ticket_snapshots : [],
    [ticketDetail?.ticket_snapshots],
  );
  const ticketStatuses = useMemo(
    () => Array.isArray(ticketDetail?.ticket_status_by_station) ? ticketDetail.ticket_status_by_station : [],
    [ticketDetail?.ticket_status_by_station],
  );
  const itemStatuses = useMemo(
    () => Array.isArray(ticketDetail?.item_status_by_item) ? ticketDetail.item_status_by_item : [],
    [ticketDetail?.item_status_by_item],
  );

  const primarySnapshot = snapshots[0] || null;
  const parsedTicket = useMemo(
    () => parseJson(primarySnapshot?.ticket_json),
    [primarySnapshot?.ticket_json]
  );

  const ticketItems = useMemo(() => toItemArray(parsedTicket), [parsedTicket]);
  const itemStatusMap = useMemo(() => {
    const map = new Map();
    for (const row of itemStatuses || []) {
      const id = row?.item_id != null ? String(row.item_id) : "";
      if (!id) continue;
      map.set(id, row);
    }
    return map;
  }, [itemStatuses]);

  const groupedCourses = useMemo(() => {
    const merged = [];
    const seenItemIds = new Set();
    let courseNum = 1;

    for (const ticketItem of ticketItems || []) {
      const itemId = ticketItem?.ID != null ? String(ticketItem.ID) : null;
      const statusMeta = itemId ? itemStatusMap.get(itemId) : null;
      const name = decodeHtml(ticketItem?.Name || statusMeta?.name || "Unknown Item");
      const category = decodeHtml(ticketItem?.Category || statusMeta?.category || "");

      if (isMessageRow(name)) {
        continue;
      }
      if (isFirePseudoRow(name)) {
        continue;
      }
      if (isCourseSeparatorRow(name, category, itemId, ticketItem)) {
        courseNum += 1;
        continue;
      }

      if (itemId) seenItemIds.add(itemId);
      const dbCourse = Number(statusMeta?.course);
      const resolvedCourse = Number.isFinite(dbCourse) ? dbCourse + 1 : courseNum;
      merged.push({
        item_id: itemId,
        course: resolvedCourse,
        name,
        category,
        quantity: Number(ticketItem?.Quantity ?? statusMeta?.quantity ?? 1) || 1,
        preModifier: decodeHtml(ticketItem?.PreModifier?.S0 || ""),
        condiments: Array.isArray(ticketItem?.Condiment) ? ticketItem.Condiment : [],
        is_addon: !!statusMeta?.is_addon || !!ticketItem?.is_addon || !!ticketItem?.isAddon,
        stations: Array.isArray(statusMeta?.stations) ? statusMeta.stations : [],
      });
    }

    for (const row of itemStatuses) {
      const itemId = row?.item_id != null ? String(row.item_id) : null;
      if (!itemId || seenItemIds.has(itemId)) continue;
      const name = decodeHtml(row?.name || "Unknown Item");
      const category = decodeHtml(row?.category || "");
      if (isMessageRow(name)) continue;
      if (isFirePseudoRow(name)) continue;
      if (isCourseSeparatorRow(name, category, itemId, null)) continue;
      const dbCourse = Number(row?.course);
      merged.push({
        item_id: itemId,
        course: Number.isFinite(dbCourse) ? dbCourse + 1 : 1,
        name,
        category,
        quantity: Number(row?.quantity ?? 1) || 1,
        preModifier: null,
        condiments: [],
        is_addon: !!row?.is_addon,
        stations: Array.isArray(row?.stations) ? row.stations : [],
      });
    }

    const lastDetectedCourse = Math.max(1, courseNum);
    for (const item of merged) {
      if (Number(item.course) === 1 && item.is_addon && !ticketItems.some((t) => String(t?.ID ?? "") === String(item.item_id ?? ""))) {
        item.course = lastDetectedCourse;
      }
    }

    const grouped = new Map();
    for (const item of merged) {
      const c = Number(item.course) || 1;
      if (!grouped.has(c)) grouped.set(c, []);
      grouped.get(c).push(item);
    }

    return [...grouped.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([course, items]) => {
        const receivedTimes = [];
        const firedTimes = [];
        const bumpedTimes = [];
        for (const item of items) {
          for (const st of item.stations || []) {
            if (st?.received_at) receivedTimes.push(st.received_at);
            if (st?.fired_at) firedTimes.push(st.fired_at);
            if (st?.bumped_at) bumpedTimes.push(st.bumped_at);
          }
        }

        const firstAppeared = receivedTimes.length
          ? receivedTimes.reduce((a, b) => (parseTimeMs(a) <= parseTimeMs(b) ? a : b))
          : null;
        const firstFired = firedTimes.length
          ? firedTimes.reduce((a, b) => (parseTimeMs(a) <= parseTimeMs(b) ? a : b))
          : null;
        const lastBumped = bumpedTimes.length
          ? bumpedTimes.reduce((a, b) => (parseTimeMs(a) >= parseTimeMs(b) ? a : b))
          : null;

        return {
          course,
          items,
          firstAppeared,
          firstFired,
          lastBumped,
          bumpDuration: formatDurationMinutes(firstAppeared, lastBumped),
        };
      });
  }, [itemStatusMap, itemStatuses, ticketItems]);

  const reportTimezone = useMemo(() => {
    if (ticketDetail?.report_timezone) return ticketDetail.report_timezone;
    const firstStationTz = ticketStatuses.find((s) => s?.station_timezone)?.station_timezone;
    return firstStationTz || "UTC";
  }, [ticketDetail?.report_timezone, ticketStatuses]);

  // Export the complete loaded model, including every course and station, regardless of scroll position.
  const downloadTicket = async () => {
    if (exporting.current) return;
    exporting.current = true;
    setExportBusy(true);
    setExportError('');
    try {
      const stationDetails = (station, defaultStatus) => {
        const zone = station.station_timezone || reportTimezone;
        return { name: stationLabel(station), details: [
          ['Status', String(station.status || defaultStatus).toUpperCase()], ['Timezone', zone],
          ['Add-on', station.is_addon ? 'Yes' : 'No'],
          ['Received', formatDateTimeAtZone(station.received_at, zone)],
          ['Parked', formatDateTimeAtZone(station.parked_at, zone)],
          ['Fired', formatDateTimeAtZone(station.fired_at, zone)],
          ['Bumped', formatDateTimeAtZone(station.bumped_at, zone)],
          ['Voided', formatDateTimeAtZone(station.voided_at, zone)],
          ['Time to Make', getTimeToMake(station.received_at, station.bumped_at)],
        ] };
      };
      // Let the loading state paint before generating a potentially multi-page document.
      await new Promise(resolve => setTimeout(resolve, 0));
      exportTicketToPDF({
        orderId: ticketDetail?.order_id ?? ticketDetail?.order_meta?.order_id,
        details: [
          ['Table', decodeHtml(parsedTicket?.GuestTable || ticketDetail?.order_meta?.table_name || '-')],
          ['Server', decodeHtml(parsedTicket?.ServerName || ticketDetail?.order_meta?.server_name || '-')],
          ['Member', decodeHtml(parsedTicket?.UserInfo || ticketDetail?.order_meta?.member_name || '-')],
          ['Report timezone', reportTimezone],
        ],
        stations: ticketStatuses.map(station => stationDetails(station, 'pending')),
        courses: groupedCourses.map(course => ({ course: course.course, details: [
          ['First Item Appeared', formatDateTimeAtZone(course.firstAppeared, reportTimezone)],
          ['First Item Fired', formatDateTimeAtZone(course.firstFired, reportTimezone)],
          ['Last Item Bumped', formatDateTimeAtZone(course.lastBumped, reportTimezone)],
          ['Course Bump Time', course.bumpDuration],
        ], items: course.items.map(item => ({ name: item.name, quantity: formatReportNumber(item.quantity), isAddon: item.is_addon,
          details: [['Item ID', item.item_id], ['Category', item.category], ['Modifier', item.preModifier],
            ['Modifiers', item.condiments.map(c => decodeHtml(c?.Name || '')).filter(Boolean).join(', ')]],
          stations: item.stations.map(station => stationDetails(station, 'active')),
        })) })),
      });
    } catch (error) {
      setExportError(error.message || 'Unable to download this ticket. Please try again.');
    } finally {
      exporting.current = false;
      setExportBusy(false);
    }
  };

  if (!snapshots.length && !itemStatuses.length && !ticketStatuses.length) {
    return <div className="text-red-600 dark:text-red-400">No ticket detail found.</div>;
  }

  return (
    <div className="font-mono font-normal text-center text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 p-4 rounded whitespace-pre-wrap leading-relaxed text-gray-900 dark:text-gray-100">
      <div className="flex flex-wrap items-center justify-end gap-2 mb-3 font-sans">
        <Tooltip title={exportBusy ? 'Preparing PDF…' : 'Download ticket PDF'}>
          <span>
            <IconButton onClick={downloadTicket} disabled={exportBusy}
              aria-label="Download ticket PDF" aria-busy={exportBusy}
              sx={{ border: '1px solid var(--report-line, #dde5ee)', borderRadius: '9px',
                color: 'var(--report-accent, #315fca)', backgroundColor: 'var(--report-raised, #f8fafd)' }}>
              <PictureAsPdfIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      {exportError && <Alert severity="error" onClose={() => setExportError('')}>{exportError}</Alert>}
      <div className="text-center border-b border-gray-400 dark:border-gray-700 pb-2 mb-3">
        <p className="text-lg font-bold">Order Ticket</p>
        <p>Order ID: {ticketDetail?.order_id || ticketDetail?.order_meta?.order_id || "-"}</p>
        <p>
          Table: {decodeHtml(parsedTicket?.GuestTable || ticketDetail?.order_meta?.table_name || "-")} | Server:{" "}
          {decodeHtml(parsedTicket?.ServerName || ticketDetail?.order_meta?.server_name || "-")}
        </p>
        <p>Member: {decodeHtml(parsedTicket?.UserInfo || ticketDetail?.order_meta?.member_name || "-")}</p>
      </div>

      <div className="mb-4">
        <p className="font-bold mb-2">Ticket Status By Station</p>
        <div className="space-y-2">
          {ticketStatuses.length ? (
            ticketStatuses.map((station) => (
              <div key={`ticket-station-${station.station_id}`} className="border border-gray-300 dark:border-gray-700 rounded p-2 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">{stationLabel(station)}</p>
                  <span className={`px-2 py-0.5 rounded text-xs border ${statusBadgeClass(station.status)}`}>
                    {String(station.status || "pending").toUpperCase()}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-4 text-xs text-gray-600 dark:text-gray-300 mt-1">
                  <div className="space-y-1 whitespace-nowrap shrink-0 text-left">
                    <p>Received: {formatDateTimeAtZone(station.received_at, station.station_timezone || reportTimezone)}</p>
                    {station.parked_at ? (
                      <p>Parked: {formatDateTimeAtZone(station.parked_at, station.station_timezone || reportTimezone)}</p>
                    ) : null}
                    <p>Bumped: {formatDateTimeAtZone(station.bumped_at, station.station_timezone || reportTimezone)}</p>
                    <p>Time to Make: {getTimeToMake(station.received_at, station.bumped_at)}</p>
                  </div>
                  {station.voided_at ? (
                    <div className="space-y-1 text-right whitespace-nowrap">
                      <p>Voided: {formatDateTimeAtZone(station.voided_at, station.station_timezone || reportTimezone)}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No station-level ticket events found.</p>
          )}
        </div>
      </div>

      <div className="border-t border-gray-300 dark:border-gray-700 pt-2 mt-2">
        <p className="font-bold mb-2">Items By Course</p>
        <div className="space-y-3">
          {groupedCourses.length ? (
            groupedCourses.map((courseGroup) => (
              <div key={`course-${courseGroup.course}`} className={`border rounded p-2 ${courseCardBgClass(courseGroup.course)}`}>
                <div className="border-b border-gray-300 dark:border-gray-700 pb-2 mb-2">
                  <p className="font-bold text-gray-900 dark:text-gray-100">Course {courseGroup.course}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    First Item Appeared: {formatDateTimeAtZone(courseGroup.firstAppeared, reportTimezone)}
                  </p>
                  {courseGroup.firstFired ? (
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      First Item Fired: {formatDateTimeAtZone(courseGroup.firstFired, reportTimezone)}
                    </p>
                  ) : null}
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    Last Item Bumped: {formatDateTimeAtZone(courseGroup.lastBumped, reportTimezone)}
                  </p>
                  <p className="text-xs text-gray-700 dark:text-gray-200 font-semibold">
                    Course Bump Time: {courseGroup.bumpDuration}
                  </p>
                </div>

                <div className="space-y-3">
                  {courseGroup.items.map((item, idx) => (
                    <div key={`${item.item_id || "item"}-${idx}`} className="rounded border border-gray-300 dark:border-gray-700 p-2 bg-white dark:bg-gray-900">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          {item.name} x {item.quantity}
                        </p>
                        {item.is_addon && (
                          <span className="px-2 py-0.5 rounded text-[10px] border bg-amber-100 text-amber-700 border-amber-300">
                            ADD-ON
                          </span>
                        )}
                      </div>
                      {item.preModifier && (
                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">Modifier: {item.preModifier}</p>
                      )}
                      {item.condiments.length > 0 && (
                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                          Modifiers: {item.condiments.map((c) => decodeHtml(c?.Name || "")).filter(Boolean).join(", ")}
                        </p>
                      )}

                      <div className="mt-2 space-y-1">
                        {item.stations.length ? (
                          item.stations.map((station) => (
                            <div
                              key={`${item.item_id || item.name}-station-${station.station_id}`}
                              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1 bg-white dark:bg-gray-900"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-gray-800 dark:text-gray-100">
                                  {stationLabel(station)}
                                </span>
                                <div className="flex items-center gap-1">
                                  {station.is_addon && (
                                    <span className="px-2 py-0.5 rounded text-[10px] border bg-amber-100 text-amber-700 border-amber-300">
                                      ADD-ON
                                    </span>
                                  )}
                                  <span className={`px-2 py-0.5 rounded text-[10px] border ${statusBadgeClass(station.status)}`}>
                                    {String(station.status || "active").toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-start justify-between gap-4 text-[11px] text-gray-600 dark:text-gray-300 mt-1">
                                <div className="space-y-1 whitespace-nowrap shrink-0 text-left">
                                  <p>Received: {formatDateTimeAtZone(station.received_at, station.station_timezone || reportTimezone)}</p>
                                  {station.parked_at ? (
                                    <p>Parked: {formatDateTimeAtZone(station.parked_at, station.station_timezone || reportTimezone)}</p>
                                  ) : null}
                                  <p>Bumped: {formatDateTimeAtZone(station.bumped_at, station.station_timezone || reportTimezone)}</p>
                                  <p>Time to Make: {getTimeToMake(station.received_at, station.bumped_at)}</p>
                                </div>
                                {(station.fired_at || station.voided_at) ? (
                                  <div className="space-y-1 text-right whitespace-nowrap">
                                    {station.fired_at ? <p>Fired: {formatDateTimeAtZone(station.fired_at, station.station_timezone || reportTimezone)}</p> : null}
                                    {station.voided_at ? (
                                    <p>Voided: {formatDateTimeAtZone(station.voided_at, station.station_timezone || reportTimezone)}</p>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-400">No station-level item events found.</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No items found.</p>
          )}
        </div>
      </div>

      <div className="text-center text-xs text-gray-400 mt-4 border-t border-dashed dark:border-gray-700 pt-2">- End of Ticket -</div>
    </div>
  );
}
